'use client';

import React from 'react';
import clsx from 'clsx';
import { UserType } from '@/@types';
import { Avatar, ChileRating, Element, Icon } from '@/components/core';
import { useLingui } from '@lingui/react';
import InternalLink from '@/components/core/components/organism/internal-link';
import { UIComponent } from '@/components/core/components';
import { useUser } from '@/components/context/user';
import useModals from '@/components/core/components/molecules/modals/use-modal';
import { useLocalizedLinks } from '@/components/core/hooks';
import { categories } from '@/@constants';
import { isProductOfType } from '@/services/api/parsers/utils';
import { Trans } from '@lingui/react/macro';
import * as styles from './feed-header.styles';
import { useFeed } from '../context/context';
import FollowButton from '../../../follow-button';

// eslint-disable-next-line no-unused-vars
type onFollowType = (user: UserType) => any;

const FeedHeader: UIComponent = ({ children }) => {
  const { user: currentUser } = useUser();
  const { product, collection, user, comment, recommendation } = useFeed();
  const { i18n } = useLingui();
  const openModal = useModals();
  const links = useLocalizedLinks();

  if (!product) {
    return null;
  }
  const category = categories[product.category.name];
  const popularity =
    product.popularity ||
    (product.extraInfo?.ratings || product.ratings || []).find((rating) => {
      return rating.source === 'chiles';
    })?.value ||
    0;
  return (
    <header className={styles.header}>
      {children || (
        <>
          <InternalLink lines={2} className={clsx(styles.title)} href={links.product(product, comment?.id || recommendation?.id)}>
            {product.name}
          </InternalLink>
          <Element as="h3" className={styles.category}>
            {i18n._(category.itemTitle)}
          </Element>
          {isProductOfType(product, ['restaurants']) && product.extraInfo?.location?.distance && (
            <div className={styles.headerRow}>
              <Trans>
                {new Intl.NumberFormat(i18n.locale, { style: 'unit', unit: 'meter', maximumFractionDigits: 0 }).format(
                  product.extraInfo.location.distance,
                )}{' '}
                away
              </Trans>
            </div>
          )}
          {(collection || popularity > 0) && (
            <Element className={styles.headerRow}>
              {collection && (
                <InternalLink className={styles.collection} href={links.collection(collection)}>
                  <Icon name="collection-square" /> <Element lines={1}>{collection.name}</Element>
                </InternalLink>
              )}
              {popularity > 0 && (
                <ChileRating
                  value={Math.round(popularity)}
                  className="cursor-pointer"
                  onClick={() => {
                    openModal({ name: 'chiles' });
                  }}
                />
              )}
            </Element>
          )}
          {user && (
            <div className={styles.headerRow}>
              <Avatar alt={`avatar ${user.nickname}`} size={30} src={user.avatar} level={user.level?.name} />
              <Element as={InternalLink} lines={1} href={links.profile(user).root} className={clsx(styles.username)}>
                {user.nickname.replace(/^@/, '')}
              </Element>
              {user.id !== currentUser?.id && <FollowButton className={styles.follow} isFollowed={user.isFollowed} />}
            </div>
          )}
          {recommendation?.createdAt && (
            <div className={styles.headerRow}>
              {new Intl.DateTimeFormat(i18n.locale, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(
                new Date(recommendation.createdAt),
              )}
            </div>
          )}
        </>
      )}
    </header>
  );
};

export default FeedHeader;
